import { useState, useEffect, useCallback } from "react";

export interface DiagnosticCollectorProps {
    loggingLevel?: 'disabled' | 'log' | 'error' | 'warn';

    logDeviceInfoSpecs?: boolean;
    logJvmSpecs?: boolean;
    logNetworkInfo?: boolean;
    logScreenDimensions?: boolean;
};

export type DiagnosticInformation = {
    userAgent?: string;
    applicationVendor?: string;

    deviceProcessor?: string;
    deviceTime?: string;
    deviceOperatingSystem?: string;
    deviceLanguage?: string;
    deviceLanguages?: string;

    deviceWidth?: number;
    deviceHeight?: number;

    screenWidth?: number;
    screenHeight?: number;

    viewportWidth?: number;
    viewportHeight?: number;

    jvmHeapTotal?: number;
    jvmHeapUsed?: number;
    jvmHeapMax?: number;

    networkDownlink?: number;
    networkType?: string | '4g' | '5g';

    additional?: {
        [name: string]: any,
    };
}

const useDiagnosticCollection = (props: DiagnosticCollectorProps) => {
    const [collectedInformation, updateCollectedInformation] = useState<DiagnosticInformation>({});

    const gatherInfo = useCallback(() => {
        const deviceInfo = {
            deviceProcessor: navigator.platform,
            deviceLanguage: navigator.language,
            deviceLanguages: navigator.languages && navigator.languages.join(' '),
        };

        const deviceScreenInfo = props.logScreenDimensions ? {
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
        } : {};

        const performanceInfo = props.logJvmSpecs ? {
            jvmHeapTotal: (performance as any)?.memory?.totalJSHeapSize ?? "Unsupported",
            jvmHeapUsed: (performance as any)?.memory?.usedJSHeapSize ?? "Unsupported",
            jvmHeapMax: (performance as any)?.memory?.jsHeapSizeLimit ?? "Unsupported",
        } : {};

        const networkInfo = {
            networkType: (navigator as any)?.connection?.effectiveType  ?? "Unsupported",
            networkDownlink: (navigator as any)?.connection?.downlink  ?? "Unsupported",
        };

        updateCollectedInformation({
            userAgent: navigator.userAgent,

            ...deviceInfo,
            ...deviceScreenInfo,
            ...performanceInfo,
            ...networkInfo,
            
        });
    }, [updateCollectedInformation]);

    const printCollectedInformation = useCallback((collectedInformation) => {
        console[props.loggingLevel]('DIAGNOSTIC: ' + btoa(JSON.stringify(collectedInformation)));
    }, [props]);

    useEffect(() => {
        if (
            Object.keys(collectedInformation).length > 0
            && props.loggingLevel
            && props.loggingLevel !== 'disabled'
        ) {
            printCollectedInformation(collectedInformation);
        }
    }, [collectedInformation]);

    useEffect(() => {
        gatherInfo();
        window.addEventListener('DOMContentLoaded', gatherInfo);

        return () => {
            window.removeEventListener('DOMContentLoaded', gatherInfo);
        };
    }, []);

    return {
        collectedInformation,
        gatherInfo,
    }
};

export default useDiagnosticCollection;